import { useState, useMemo, useEffect } from 'react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getStripeForConnect } from '@/lib/stripe';
import { stripeElementsLocale } from '@/lib/stripeLocale';
import type { PaymentIntentResult } from '@stripe/stripe-js';
import * as Sentry from '@sentry/react';
import { Loader2, X, Lock, ShieldCheck, AlertCircle } from 'lucide-react';

interface PaymentSheetProps {
  clientSecret: string;
  total: number;
  subtotal?: number;
  serviceFee?: number;
  primaryColor: string;
  stripeAccount?: string | null;
  onSuccess: () => void;
  onError: (message: string) => void;
  onClose: () => void;
  t: (key: string, options?: Record<string, string>) => string;
  /** Where a redirect-based method (MobilePay, iDEAL…) returns to. Defaults to the current URL. */
  returnUrl?: string;
  /** The fan page's language; Stripe's own texts in the form follow it (phone language when Stripe has none). */
  locale?: string | null;
}

const LOAD_WATCHDOG_MS = 15_000;

function CheckoutForm({
  total,
  onSuccess,
  onError,
  t,
  returnUrl,
}: Omit<PaymentSheetProps, 'clientSecret' | 'onClose' | 'primaryColor'>) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  // Pay stays off until the Payment Element has mounted (onReady) and reports
  // a complete form (onChange). The client secret exists up front, so
  // confirmPayment needs no elements.submit() first.
  const [ready, setReady] = useState(false);
  const [complete, setComplete] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Stripe captured the payment but the page's own follow-up threw: the sheet
  // can't close itself, and Pay must never come back (a second tap would
  // charge twice).
  const [paidButStuck, setPaidButStuck] = useState(false);
  // Watchdog: if the Payment Element never reports ready (Stripe.js blocked,
  // getStripeForConnect resolved null, a hung iframe) the button would say
  // "Loading secure payment…" forever — after 15 s tell the fan instead.
  // Cleared once ready and on unmount, so it never fires late.
  const [loadTimedOut, setLoadTimedOut] = useState(false);
  useEffect(() => {
    if (ready) return;
    const id = setTimeout(() => setLoadTimedOut(true), LOAD_WATCHDOG_MS);
    return () => clearTimeout(id);
  }, [ready]);
  const loadErrorMessage = t('payment.loadError', { defaultValue: "Couldn't load the payment form — close and try again." });
  const shownError = errorMessage ?? (loadTimedOut && !ready ? loadErrorMessage : null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    // Enter submits the form even when the button is disabled.
    if (!ready || !complete) return;

    setProcessing(true);
    setErrorMessage(null);

    let result: PaymentIntentResult;
    try {
      result = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: returnUrl ?? window.location.href,
        },
        redirect: 'if_required',
      });
    } catch (err: any) {
      // Defense-in-depth: confirmPayment can throw on network / SDK / unexpected
      // shape errors. Without this catch, processing stays true and the Pay
      // button is stuck forever.
      console.error('[PaymentSheet] confirmPayment threw:', err);
      const msg = err?.message || t('payment.genericError', { defaultValue: 'Payment failed. Please try again.' });
      setErrorMessage(msg);
      onError(msg);
      setProcessing(false);
      return;
    }

    if (result.error) {
      const msg = result.error.message || t('payment.genericError', { defaultValue: 'Payment failed. Please try again.' });
      setErrorMessage(msg);
      onError(msg);
      setProcessing(false);
      return;
    }
    if (result.paymentIntent?.status !== 'succeeded') {
      const msg = `Unexpected payment state: ${result.paymentIntent?.status || 'unknown'}`;
      setErrorMessage(msg);
      onError(msg);
      setProcessing(false);
      return;
    }

    // Stripe has confirmed the payment: the money is taken. Whatever the page
    // does next is bookkeeping and must never be reported through onError —
    // the caller treats that as "not paid" (abandons the order, forgets it).
    // This used to share the try above, and a crash while building the
    // display lines abandoned a paid order (HJK, 4 Oct 2026).
    try {
      onSuccess();
    } catch (err) {
      console.error('[PaymentSheet] onSuccess threw after a captured payment:', err);
      Sentry.captureException(err, { tags: { stage: 'post-payment' } });
      setProcessing(false);
      setPaidButStuck(true);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
      <PaymentElement
        onReady={() => setReady(true)}
        onLoadError={(e) => setErrorMessage(e.error?.message ?? loadErrorMessage)}
        onChange={(e) => setComplete(e.complete)}
        options={{
          layout: {
            type: 'accordion',
            defaultCollapsed: true,
            radios: 'never',
            spacedAccordionItems: true,
          },
        }}
      />

      {shownError && (
        <div className="p-3 sm:p-3.5 rounded-xl bg-red-500/10 border border-red-400/30 text-red-200 text-[13px] sm:text-sm flex items-start gap-2.5">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span className="leading-relaxed">{shownError}</span>
        </div>
      )}

      {paidButStuck && (
        <div className="p-3 sm:p-3.5 rounded-xl bg-emerald-500/15 border border-emerald-400/30 text-emerald-100 text-[13px] sm:text-sm flex items-start gap-2.5">
          <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span className="leading-relaxed">{t('payment.receivedReload', { defaultValue: 'Payment received — reload the page to see your order.' })}</span>
        </div>
      )}

      {/* Pay button */}
      <button
        type="submit"
        disabled={!stripe || !elements || !ready || !complete || processing || paidButStuck}
        className="group relative w-full h-[52px] sm:h-14 rounded-2xl font-semibold text-[15px] sm:text-base flex items-center justify-center gap-2.5 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed bg-white text-gray-900 hover:bg-gray-50 active:scale-[0.985] shadow-[0_8px_24px_-8px_rgba(255,255,255,0.25)] tracking-tight"
        style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", system-ui, sans-serif' }}
      >
        {!ready ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin text-gray-700" />
            <span>{t('payment.loadingSecure', { defaultValue: 'Loading secure payment…' })}</span>
          </>
        ) : processing ? (
          <Loader2 className="h-5 w-5 animate-spin text-gray-700" />
        ) : (
          <>
            <Lock className="w-[15px] h-[15px] opacity-90" strokeWidth={2.5} />
            <span>{t('payment.pay', { defaultValue: 'Pay' })}</span>
            <span className="font-bold tabular-nums">€{total.toFixed(2)}</span>
          </>
        )}
      </button>

      {/* Security badge */}
      <div className="flex items-center justify-center pt-1">
        <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/10">
          <ShieldCheck className="w-3.5 h-3.5 text-emerald-400/90" strokeWidth={2.25} />
          <span className="text-[10.5px] sm:text-[11px] text-white/55 font-medium tracking-[0.04em] uppercase">
            {t('payment.securedByStripe', { defaultValue: 'Secured by Stripe' })}
          </span>
        </div>
      </div>
    </form>
  );
}

export function PaymentSheet({
  clientSecret,
  total,
  subtotal,
  serviceFee,
  primaryColor,
  stripeAccount,
  onSuccess,
  onError,
  onClose,
  t,
  returnUrl,
  locale,
}: PaymentSheetProps) {
  const showBreakdown = typeof serviceFee === 'number' && serviceFee > 0 && typeof subtotal === 'number';
  const stripeInstance = useMemo(() => getStripeForConnect(stripeAccount || null), [stripeAccount]);

  // Modern system font stack — looks crisp on iOS, macOS, Windows, Android
  const modernFontStack = '-apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", Roboto, system-ui, sans-serif';

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/75 backdrop-blur-[10px] animate-in fade-in duration-200"
        onClick={onClose}
      />

      {/* Sheet */}
      <div
        className="relative w-full sm:max-w-[440px] sm:rounded-3xl rounded-t-[28px] overflow-hidden animate-in slide-in-from-bottom-4 sm:zoom-in-95 duration-300 ease-out max-h-[92vh] sm:max-h-[90vh] flex flex-col shadow-2xl"
        style={{
          background: `radial-gradient(ellipse 120% 80% at top, ${primaryColor}f5 0%, ${primaryColor}cc 35%, #0a0a14 100%)`,
          fontFamily: modernFontStack,
        }}
      >
        {/* Mobile drag handle */}
        <div className="flex justify-center pt-2.5 pb-1 sm:hidden flex-shrink-0">
          <div className="w-9 h-[3px] rounded-full bg-white/25" />
        </div>

        {/* Close button */}
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3.5 sm:top-5 right-3.5 sm:right-5 w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-white/[0.06] border border-white/10 backdrop-blur-sm flex items-center justify-center text-white/55 hover:text-white hover:bg-white/[0.12] active:scale-95 transition-all duration-200 z-10"
        >
          <X className="w-4 h-4" strokeWidth={2.25} />
        </button>

        {/* Scrollable content */}
        <div className="overflow-y-auto overscroll-contain px-5 sm:px-7 pt-3 sm:pt-6 pb-7 sm:pb-8 flex-1">
          {/* Header */}
          <div className="text-center mb-6 sm:mb-7">
            <div className="inline-flex items-center justify-center w-11 h-11 sm:w-12 sm:h-12 rounded-2xl bg-white/[0.08] border border-white/[0.08] mb-3 backdrop-blur-sm">
              <Lock className="w-[18px] h-[18px] sm:w-5 sm:h-5 text-white/85" strokeWidth={2.25} />
            </div>
            <h2
              className="text-white text-[22px] sm:text-2xl font-semibold tracking-[-0.02em] leading-tight"
              style={{ fontFamily: modernFontStack }}
            >
              {t('payment.title', { defaultValue: 'Secure checkout' })}
            </h2>
            <p className="text-white/50 text-[13px] sm:text-sm mt-1.5 font-normal tracking-tight">
              {t('payment.enterDetails', { defaultValue: 'Pay securely with card or wallet' })}
            </p>
          </div>

          {/* Order summary breakdown (only when there's a service fee to itemise) */}
          {showBreakdown && (
            <div className="mb-5 sm:mb-6 rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-sm p-4 sm:p-4 space-y-2">
              <div className="flex items-center justify-between text-[13px] sm:text-sm text-white/65">
                <span>{t('payment.subtotal', { defaultValue: 'Subtotal' })}</span>
                <span className="tabular-nums">€{(subtotal as number).toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between text-[13px] sm:text-sm text-white/65">
                <span>{t('payment.serviceFee', { defaultValue: 'Service fee' })}</span>
                <span className="tabular-nums">€{(serviceFee as number).toFixed(2)}</span>
              </div>
              <div className="h-px bg-white/10 my-1" />
              <div className="flex items-center justify-between text-[15px] sm:text-base text-white font-semibold">
                <span>{t('payment.total', { defaultValue: 'Total' })}</span>
                <span className="tabular-nums">€{total.toFixed(2)}</span>
              </div>
            </div>
          )}

          {/* Stripe Elements */}
          <Elements
            stripe={stripeInstance}
            options={{
              clientSecret,
              locale: stripeElementsLocale(locale),
              appearance: {
                theme: 'night',
                variables: {
                  colorPrimary: '#ffffff',
                  colorBackground: '#1a1c24',
                  colorText: '#f5f5f8',
                  colorTextSecondary: 'rgba(255, 255, 255, 0.5)',
                  colorTextPlaceholder: 'rgba(255, 255, 255, 0.28)',
                  colorDanger: '#f87171',
                  colorIcon: 'rgba(255, 255, 255, 0.7)',
                  borderRadius: '14px',
                  fontFamily: modernFontStack,
                  spacingUnit: '5px',
                  fontSizeBase: '15px',
                  fontWeightNormal: '500',
                  fontWeightMedium: '500',
                  fontWeightBold: '600',
                  fontLineHeight: '1.4',
                },
                rules: {
                  // ── Inputs ─────────────────────────────────────────
                  '.Input': {
                    border: '1px solid rgba(255, 255, 255, 0.10)',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    color: '#f5f5f8',
                    padding: '14px 16px',
                    fontSize: '15px',
                    fontWeight: '500',
                    boxShadow: 'none',
                    transition: 'border-color 0.18s ease, background-color 0.18s ease, box-shadow 0.18s ease',
                  },
                  '.Input:focus': {
                    border: '1px solid rgba(255, 255, 255, 0.45)',
                    boxShadow: '0 0 0 4px rgba(255, 255, 255, 0.08)',
                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  },
                  '.Input:hover': {
                    border: '1px solid rgba(255, 255, 255, 0.20)',
                    backgroundColor: 'rgba(255, 255, 255, 0.07)',
                  },
                  '.Input--invalid': {
                    border: '1px solid rgba(248, 113, 113, 0.55)',
                    boxShadow: '0 0 0 3px rgba(248, 113, 113, 0.10)',
                  },
                  // ── Labels ─────────────────────────────────────────
                  '.Label': {
                    color: 'rgba(255, 255, 255, 0.55)',
                    fontSize: '12px',
                    fontWeight: '600',
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    marginBottom: '8px',
                  },
                  // ── Accordion rows (the "dropdown list" of methods) ──
                  // Stripe rejects `transition` + `boxShadow` here so we keep it simple.
                  '.AccordionItem': {
                    border: '1px solid rgba(255, 255, 255, 0.10)',
                    backgroundColor: 'rgba(255, 255, 255, 0.04)',
                    borderRadius: '14px',
                    padding: '14px 16px',
                  },
                  '.AccordionItem:hover': {
                    border: '1px solid rgba(255, 255, 255, 0.20)',
                    backgroundColor: 'rgba(255, 255, 255, 0.07)',
                  },
                  '.AccordionItem--selected': {
                    border: '1px solid rgba(255, 255, 255, 0.30)',
                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  },
                  // ── Pickers (used in overflow dropdowns + sub-method selectors) ──
                  // Force readable colors so Stripe's default never shows white-on-white.
                  '.PickerItem': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.10)',
                    color: '#f5f5f8',
                    borderRadius: '12px',
                    padding: '12px 14px',
                    fontWeight: '500',
                  },
                  '.PickerItem:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.10)',
                    border: '1px solid rgba(255, 255, 255, 0.22)',
                    color: '#ffffff',
                  },
                  '.PickerItem--selected': {
                    backgroundColor: 'rgba(255, 255, 255, 0.14)',
                    border: '1px solid rgba(255, 255, 255, 0.32)',
                    color: '#ffffff',
                  },
                  '.PickerItem--selected:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.18)',
                  },
                  '.PickerItemIcon': {
                    fill: 'rgba(255, 255, 255, 0.75)',
                  },
                  '.PickerItemIcon--selected': {
                    fill: '#ffffff',
                  },
                  '.PickerItemLabel': {
                    color: 'inherit',
                  },
                  // ── Tabs (kept in case Stripe falls back to tabs for any sub-flow) ──
                  '.Tab': {
                    border: '1px solid rgba(255, 255, 255, 0.10)',
                    backgroundColor: 'rgba(255, 255, 255, 0.04)',
                    color: 'rgba(255, 255, 255, 0.65)',
                    borderRadius: '12px',
                    padding: '12px 10px',
                    fontWeight: '600',
                    fontSize: '13px',
                    transition: 'border-color 0.18s ease, background-color 0.18s ease, color 0.18s ease',
                  },
                  '.Tab:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                    color: 'rgba(255, 255, 255, 0.85)',
                    border: '1px solid rgba(255, 255, 255, 0.18)',
                  },
                  '.Tab--selected': {
                    backgroundColor: 'rgba(255, 255, 255, 0.14)',
                    color: '#ffffff',
                    border: '1px solid rgba(255, 255, 255, 0.32)',
                  },
                  '.TabIcon': {
                    fill: 'rgba(255, 255, 255, 0.7)',
                  },
                  '.TabIcon--selected': {
                    fill: '#ffffff',
                  },
                  // ── Errors ──
                  '.Error': {
                    color: '#fca5a5',
                    fontSize: '12.5px',
                    fontWeight: '500',
                    marginTop: '6px',
                  },
                  // ── Misc (text/dropdown blocks Stripe renders inside the form) ──
                  '.Text': {
                    color: 'rgba(255, 255, 255, 0.70)',
                  },
                  '.TextLink': {
                    color: '#ffffff',
                  },
                  '.Block': {
                    backgroundColor: 'rgba(255, 255, 255, 0.04)',
                    border: '1px solid rgba(255, 255, 255, 0.10)',
                    color: '#f5f5f8',
                  },
                  '.Dropdown': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.10)',
                    color: '#f5f5f8',
                  },
                  '.DropdownItem': {
                    backgroundColor: 'transparent',
                    color: '#f5f5f8',
                    padding: '10px 14px',
                  },
                  '.DropdownItem:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.10)',
                  },
                  '.DropdownItem--highlight': {
                    backgroundColor: 'rgba(255, 255, 255, 0.14)',
                    color: '#ffffff',
                  },
                },
              },
            }}
          >
            <CheckoutForm
              total={total}
              primaryColor={primaryColor}
              onSuccess={onSuccess}
              onError={onError}
              t={t}
              returnUrl={returnUrl}
            />
          </Elements>
        </div>
      </div>
    </div>
  );
}
